import '../styles/FamilyTree.css';
import {useState, useMemo, useEffect, useRef, useLayoutEffect} from 'react';
import {usePeople, useMediaUrl} from '../hooks/hooks_index';
import {MediaIcon, MediaImage} from '../components/components_index'
import {Link} from 'react-router-dom';
import {hierarchy, tree} from 'd3-hierarchy';
import {zoom, zoomIdentity, zoomTransform} from 'd3-zoom';
import {select} from 'd3-selection';

// const CALC_SPOUSE = 60;
const SPOUSE_OFFSET = 120;
const NODE_WIDTH = 30;
const NODE_GAP = 100;
const CHILD_DIST = 75;
const BOX_HEIGHT = 112;
const BOX_WIDTH = 90;
const IMG_VERT_OFFSET = -50
const TEXT_VERT_OFFSET = 37
const IMG_RAD = 37;

export default function FamilyTree() {
  const {data: people, isLoading: peopleLoading, error: peopleError} = usePeople();
  const peopleList = useMemo(() => (people ? Object.entries(people) : []), [people]);

  const [boxInfo, setBoxInfo] = useState(null);

  const [fullScreen, setFullScreen] = useState(false);

  const svgRef = useRef(null);
  const svgSizeRef = useRef(null);
  const gRef = useRef(null);

  useEffect(() => {
      if (!people) return;
      async function fetchBoxes() {
        const boxes = await Promise.all(
          peopleList
            .filter(([id, person]) => !id.startsWith("P0") && (!id.startsWith("P9") || id === "P900000" || id === "P900001"))
            .map(async ([id, person]) => {  
              return {
                id,
                name: person.AKA || `${person.f_name} ${person.l_name}`,
                img: person.profile_pic ?? "S_logo_PNG.png",
                children: Array.isArray(person.children) ? person.children : [],
                parents: {father: person.parents?.f_id ?? null, mother: person.parents?.m_id ?? null},
                b_details: {
                  b_year: person.b_details?.b_year ?? null,
                  b_month: person.b_details?.b_month ?? null,
                  b_day: person.b_details?.b_day ?? null
                },
                d_details: {
                  d_year: person.d_details?.d_year ?? null,
                  d_month: person.d_details?.d_month ?? null,
                  d_day: person.d_details?.d_day ?? null
                },
                spouses: Array.isArray(person.m_details)
                  ? person.m_details.map(m => ({
                    id: m.spouse,
                    married: m.m_year ?? null,
                    divorced: m.div_year ?? null
                  }))
                  : []
              };
            })
        );
        setBoxInfo(boxes);
      }
      fetchBoxes();
    }, [peopleList, people]);


  
  const boxInfoById = useMemo(() => {
    if (!Array.isArray(boxInfo)) return new Map();
    return new Map(boxInfo.map(person => [person.id, person]));
  }, [boxInfo]);


  const [selectedID, setSelectedID] = useState(null);
  const root = useMemo(() => buildFamilyTreeLayout(boxInfo), [boxInfo]);

  const positionedNodes = useMemo(() => {
    if (!root) return new Map();

    const map = new Map();

    root.descendants().forEach(node => {
      // Base node  
      // node.data.id==="P311000" ? console.log(node.x) : null;
      map.set(node.data.id, {
        node,
        x: node.x,
        y: node.y
      });
    });

    root.descendants().forEach(node => {
      const spouses = node.data.spouses || [];
      if (spouses.length === 0) return;

      const lastSpouse = spouses[spouses.length - 1];
      const isDivorced = lastSpouse.divorced;

      if (isDivorced) return;

      const spouseNode = map.get(lastSpouse.id);
      if (!spouseNode) return;

      // console.log(node)

      map.set(lastSpouse.id, {
        node: spouseNode.node,
        x: node.x + SPOUSE_OFFSET,
        y: node.y
      });
    });

    return map;
  }, [root]);

  const zoomBehaviorRef = useRef();

  if (!zoomBehaviorRef.current) {
    zoomBehaviorRef.current = zoom()
      .filter((event) => {
        if (event.type === 'dblclick') return false;
        return true
      })
      .on("zoom", (event) => {
        select(gRef.current).attr("transform", event.transform);
      });
  }

  const zoomBehavior = zoomBehaviorRef.current;

  const zoomIn = () => {
    const svg = select(svgRef.current);
    svg.transition().duration(300).call(zoomBehavior.scaleBy, 1.3);
  };

  const zoomOut = () => {
    const svg = select(svgRef.current);
    svg.transition().duration(300).call(zoomBehavior.scaleBy, 0.7);
  };

  const resetZoom = () => {
    if (!root || !svgRef.current) return;

    const svg = select(svgRef.current);
    const nodes = root.descendants();

    const {scale, translateX, translateY} = maxZoom(svgRef.current, nodes)

    const transform = zoomIdentity
      .translate(translateX, translateY)
      .scale(scale);

    svg.transition().duration(500).call(zoomBehavior.transform, transform);
  };


  useEffect(() => {
    // if (!selectedID || !positionedNodes.has(selectedID)) return;

    // const {x, y} = positionedNodes.get(selectedID);
    if (!selectedID) return;

    const { x, y } = selectedID;

    const svg = select(svgRef.current);
    const width = svgRef.current.clientWidth;
    const height = svgRef.current.clientHeight;

    svg.call(zoomBehavior);

    const scale = 1.2; 

    const transform = 
      zoomIdentity
        .translate(width / 2 - x * scale, height / 2 - y * scale)
        .scale(scale);

    svg
      .transition()
      .duration(600)
      .call(zoomBehavior.transform, transform);

  }, [selectedID, positionedNodes]);
  
  useLayoutEffect(() => {
    const svg = select(svgRef.current);

    svg.call(zoomBehavior);

    svg.on("wheel", (event) => event.preventDefault());

    return () => {
      svg.on(".zoom", null);
      svg.on(".wheel", null)
    };
  }, []);

  useEffect(() => {
    if (!root || !svgRef.current || !gRef.current) return;

    const svg = select(svgRef.current);
    const nodes = root.descendants();

    const {scale, translateX, translateY} = maxZoom(svgRef.current, nodes)

    zoomBehavior.scaleExtent([scale, 2.4])

    const transform = zoomIdentity
      .translate(translateX, translateY)
      .scale(scale);

    svg.call(zoomBehavior).call(zoomBehavior.transform, transform);
  }, [root]);

  useLayoutEffect(() => {
    if (!svgRef.current) return;

    const svg = select(svgRef.current);

    const width = svgRef.current.clientWidth;
    const height = svgRef.current.clientHeight;

    if (svgSizeRef.current) {
      const transform = zoomTransform(svgRef.current);

      const dx = (width - svgSizeRef.current.width) / 2;
      const dy = (height - svgSizeRef.current.height) / 2;

      svg.call(
        zoomBehavior.transform,
        zoomIdentity
          .translate(transform.x + dx, transform.y + dy)
          .scale(transform.k)
      );
    }
      svgSizeRef.current = {width, height};
    }, [fullScreen]);

  return (
    <div className={`family-tree-page ${fullScreen?'full-screen':''}`}>
      <div className="page-title">Family Tree</div>
      <div className="family-tree-container">
        <FullScreen fullScreen={fullScreen} setFullScreen={setFullScreen}/>
        <div className="zoom-controls" onClick={() => setSelectedID(null)}>
          <div>
            <button onClick={zoomIn} className='in-button'>{'\u002B'}</button>
          </div>
          <div>
            <button onClick={zoomOut} className='out-button'>{'\u2013'}</button>
          </div>
          <div>
            <button onClick={resetZoom} className='home-button'>{'\u25C8'}</button>
          </div>
        </div>
        <div className='svg-container'>
          <svg ref={svgRef} className='tree-svg' style={{touchAction:'none'}} onClick={() => setSelectedID(null)}> 
            <g ref={gRef}>
              {root && (
                <g>                  
                  {root.descendants() // Horizontal line between spouses
                    .filter(node => isBloodRelative(node.data.id))
                    .flatMap(personNode => {
                      const spousePositions = getSpousePositions(personNode, positionedNodes);
                      if (spousePositions.length === 0) return [];
                      const lastSpouse = spousePositions[spousePositions.length - 1]
                      if (lastSpouse.divorced) return [];

                      return spousePositions.map(spouse => (
                        <line
                          key={`marriage-${personNode.data.id}-${spouse.id}`}
                          x1={personNode.x}
                          y1={personNode.y}
                          x2={spouse.x}
                          y2={spouse.y}
                          stroke="#64748b"
                          strokeWidth={2}
                        />
                      ));
                    })}

                  {root.descendants() // Vertical line from parents
                    .filter(node => isBloodRelative(node.data.id))
                    .flatMap(personNode => {
                      if (!personNode.children || personNode.children.length === 0) return [];

                      const spousePositions = getSpousePositions(personNode, positionedNodes);
                      const points = [
                        { x: personNode.x },
                        ...spousePositions
                      ];

                      const xValues = points.map(p => p.x);
                      const minX = Math.min(...xValues);
                      const maxX = Math.max(...xValues);

                      const isSpecialArray = ["P900000", "P400000", "P500000", "P130000", "P140000", "P310000", "P121000"]
                      const isSpecial = isSpecialArray.includes(personNode.data.id);

                      const centerX = isSpecial
                        ? (minX + maxX) / 2
                        : minX;

                      const topY = personNode.y;
                      const childY = personNode.children[0].y - CHILD_DIST;

                      return (
                        <line
                          key={`trunk-${personNode.data.id}`}
                          x1={centerX}
                          y1={topY}
                          x2={centerX}
                          y2={childY}
                          stroke="#64748b"
                          strokeWidth={2}
                        />
                      );
                    })}
                    
                  {root.descendants() // Horizontal and vertical line to children
                    .filter(node => isBloodRelative(node.data.id))
                    .flatMap(personNode => {
                      if (!personNode.children || personNode.children.length === 0) return [];

                      const spousePositions = getSpousePositions(personNode, positionedNodes);
                      const points = [
                        { x: personNode.x },
                        ...spousePositions
                      ];

                      // const centerX =
                      //   (Math.min(...points.map(p => p.x)) +
                      //   Math.max(...points.map(p => p.x))) / 2;

                      const branchY = personNode.children[0].y - CHILD_DIST;

                      return [
                        // Horizontal branch
                        <line
                          key={`children-branch-${personNode.data.id}`}
                          x1={Math.min(...personNode.children.map(c => c.x))}
                          y1={branchY}
                          x2={Math.max(...personNode.children.map(c => c.x))}
                          y2={branchY}
                          stroke="#64748b"
                          strokeWidth={2}
                        />,

                        // Vertical drops
                        ...personNode.children.map(child => (
                          <line
                            key={`child-drop-${personNode.data.id}-${child.data.id}`}
                            x1={child.x}
                            y1={branchY}
                            x2={child.x}
                            y2={child.y}
                            stroke="#64748b"
                            strokeWidth={2}
                          />
                        ))
                      ];
                    })}

                  {root.descendants().filter(node => isBloodRelative(node.data.id)).map(node => (
                    <g
                      key={node.data.id}
                      transform={`translate(${node.x},${node.y})`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedID({
                          id: node.data.id,
                          x: node.x,
                          y: node.y
                        });
                      }}
                      className="blood-rel-box"
                    >
                      <rect
                        x={-0.5*BOX_WIDTH}
                        y={-0.5*BOX_HEIGHT}
                        width={BOX_WIDTH}
                        height={BOX_HEIGHT}
                        rx={12}
                        className={node.data.id === selectedID?.id ? "selected" : ""}
                      />
                      {node.data.img && (
                        <MediaImageTree mediaID={node.data.img} bloodRel={true} />
                      )}
                      <text className="blood-rel-text" textAnchor="middle" y={TEXT_VERT_OFFSET}>
                        <tspan x="0" dy="0">
                          {/* {`${node.data.name.trim().split(" ")[0]} ${node.x.toFixed(1)}`} */}
                          {node.data.name.trim().split(" ")[0]}
                        </tspan>
                        {node.data.name.trim().split(" ")[1] && (
                          <tspan x="0" dy="1.2em">
                            {node.data.name.trim().split(" ").slice(1).join(" ")}
                          </tspan>
                        )}
                      </text>
                    </g>
                  ))}

                  {root // Spouses
                    .descendants()
                    .filter(node => isBloodRelative(node.data.id))
                    .flatMap(node => {
                      const person = node.data;

                      if (!person.spouses || person.spouses.length === 0) return [];

                      const lastSpouse = person.spouses[person.spouses.length - 1];

                      const isDivorced = lastSpouse.divorced;
                      if (isDivorced) return [];

                      const spouseNode = positionedNodes.get(lastSpouse.id);
                      if (!spouseNode) return []

                      // console.log(node)
                      const x = node.x + SPOUSE_OFFSET;
                      const y = node.y;

                      return (
                        <g
                          key={`${person.id}-spouse-${lastSpouse.id}`}
                          transform={`translate(${x},${y})`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedID({
                              id: spouseNode.node.data.id,
                              x: x,
                              y: y
                            });
                          }}
                          className={`blood-rel-box spouse-box`}
                        >
                          <rect
                            x={-0.5*BOX_WIDTH}
                            y={-0.5*BOX_HEIGHT}
                            width={BOX_WIDTH}
                            height={BOX_HEIGHT}
                            rx={12}
                            className={lastSpouse.id === selectedID?.id ? "selected" : ""}
                          />
                          {spouseNode.node.data.img && (
                            <MediaImageTree mediaID={spouseNode.node.data.img} bloodRel={false} />
                          )}
                          <text className="blood-rel-text" textAnchor="middle" y={TEXT_VERT_OFFSET}>
                            <tspan x="0" dy="0">
                              {spouseNode.node.data.name.trim().split(" ")[0]}
                            </tspan>
                            {spouseNode.node.data.name.trim().split(" ")[1] && (
                              <tspan x="0" dy="1.2em">
                                {spouseNode.node.data.name.trim().split(" ").slice(1).join(" ")}
                              </tspan>
                            )}
                          </text>
                        </g>
                      );
                    })
                  }
                </g>
              )}
            </g>
          </svg>
          {selectedID && (() => {
            const person = boxInfoById.get(selectedID.id);
            if (!person) return null;
            // const screenPos = svgPointToContainer(selectedID.x, selectedID.y);

            return(
              <PersonPopup
                person={person}
                onClose={() => setSelectedID(null)}
              />
            );
          })()}
        </div>
      </div>
    </div>
  );
}

const isBloodRelative = (id) => id.endsWith("0");

function buildFamilyHierarchy(people) {
  if (!people || people.length === 0) return null;

  // 1. Create node map
  const nodes = new Map();

  people.forEach(person => {
    nodes.set(person.id, {
      ...person,
      children: []
    });
  });

  const hasParent = new Set();
  nodes.forEach(person => {
    const fatherId = person.parents?.father;
    const motherId = person.parents?.mother;

    let parentID = null;

    if (fatherId && isBloodRelative(fatherId)) {
      parentID = fatherId;
    } else if (motherId && isBloodRelative(motherId)) {
      parentID = motherId;
    }

    if (parentID && nodes.has(parentID)) {
      nodes.get(parentID).children.push(person);
      hasParent.add(person.id);
    }
  });

  const roots = [];
  nodes.forEach(person => {
    if (!hasParent.has(person.id)) {
      roots.push(person);
    }
  });

  const syntheticRoot = {
    id: "__root__",
    name: "Family Root",
    children: roots
  };

  return hierarchy(syntheticRoot, d => d.children);
}

function buildFamilyTreeLayout(boxInfo) {
  if (!boxInfo || boxInfo.length === 0) return null;

  const root = buildFamilyHierarchy(boxInfo);
  if (!root) return null;

  computeSubtreeWidth(root);

  const layout = tree()
    .nodeSize([NODE_WIDTH,180])
    .separation((a, b) => {
      return (a.subtreeWidth + b.subtreeWidth + NODE_GAP) / (NODE_WIDTH);
    });
  layout(root);

  const childOfSpecial = [
    "P410000", "P420000", "P510000", "P520000", "P131000", "P132000", "P133000", "P141000", "P142000", "P143000", "P311000", "P121100", "P121200"
  ]
  const specialShift = [
    "P200000", "P110000", "P120000"
  ]

  root.descendants().forEach(node => {
    if (childOfSpecial.includes(node.data.id)) {
      node.descendants().forEach(d => {
        d.x += SPOUSE_OFFSET/2;
      });
    }
    if (specialShift.includes(node.data.id)) {
      node.descendants().forEach(d => {
        d.x += SPOUSE_OFFSET/2;
      });
    }
    if (node.data.id === "P110000") {
      node.descendants().forEach(d => {
        d.x += SPOUSE_OFFSET/2;
      });
    }
    
  })
  
  return root;
}

function computeSubtreeWidth(node) {
  const selfWidth = nodeVisualWidth(node);

  if (!node.children || node.children.length === 0) {
    node.subtreeWidth = selfWidth;
    return selfWidth
  }

  const childrenWidth =
    node.children
      .map(computeSubtreeWidth)
      .reduce((sum, w) => sum + w, 0);

  node.subtreeWidth = Math.max(selfWidth, childrenWidth);

  // if (node.data.id.endsWith("00000")) {
  //   console.log(node.data.id);
  //   console.log(selfWidth);
  //   console.log(childrenWidth);
  //   console.log(node.subtreeWidth);
  // }

  return node.subtreeWidth;
}

function nodeVisualWidth(node) {
  const spouses = node.data.spouses || [];
  let width = NODE_WIDTH;

  if (spouses.length > 0) {
    const lastSpouse = spouses[spouses.length - 1];
    if (!lastSpouse.divorced) {
      width += SPOUSE_OFFSET;
    }
  }

  return width;
}

function getSpousePositions(personNode, positionedNodes) {
  const spouses = personNode.data.spouses || [];

  return spouses
    .map((spouse, index) => {
      const spouseNode = positionedNodes.get(spouse.id);
      if (!spouseNode) return null;

      // console.log(personNode.x + SPOUSE_OFFSET)
      return {
        bruh: spouse.id,
        x: personNode.x + SPOUSE_OFFSET,
        y: personNode.y,
        divorced: spouse.divorced != null
      };
    })
    .filter(Boolean);
}

function maxZoom(svgEl, nodes) {  //big problem code, may need to fix way down the line, but works for now
  const blood_nodes = nodes.filter((n) => isBloodRelative(n.data.id));

  const maxXNode = blood_nodes.reduce((max, node) =>
    node.x > max.x ? node : max
  );

  // console.log(maxXNode.data.spouses)

  const minX = Math.min(...blood_nodes.map(d => d.x));
  // const maxX = Math.max(...blood_nodes.map(d => d.x));
  const maxX = (maxXNode.data.spouses.length !== 0) ? (maxXNode.x + SPOUSE_OFFSET) : maxXNode.x;
  const minY = Math.min(...blood_nodes.map(d => d.y));
  const maxY = Math.max(...blood_nodes.map(d => d.y));

  

  const treeWidth = maxX - minX;
  const treeHeight = maxY - minY;

  // console.log(`min ${minX}`)
  // console.log(`max ${maxX}`)  
  // console.log(`tree width: ${treeWidth}`)
  // console.log(nodes)


  const svgWidth = svgEl.clientWidth;
  const svgHeight = svgEl.clientHeight;

  const padding = 160;

  const scale = Math.min(
    svgWidth / (treeWidth + padding),
    svgHeight / (treeHeight + padding),
    1.2
  );

  const translateX =
    svgWidth / 2 - (minX + treeWidth / 2) * scale;
  const translateY =
    svgHeight / 2 - (minY + treeHeight / 2) * scale;

  return {scale, translateX, translateY};
}

function PersonPopup({person, onClose}) {
  if (!person) return null;

  const b = person.b_details;
  const d = person.d_details;

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December"
  ];

  return (
    <div
      className='person-popup'
      onClick={onClose}
    >
      <div className='popup-box' onClick={e => e.stopPropagation()}>
        <button className='close-popup' onClick={onClose}>{'\u00D7'}</button>
        <MediaImage className='popup-img' mediaID={person.img} alt={person.name}/>
        <div className='popup-content'>
          <Link to={`/people/${person.id}`}>{person.name}</Link>
          {b.b_year &&
            <div>Born: {b.b_month ? `${monthNames[b.b_month-1]} ` : ''}{b.b_day ? `${b.b_day}, ` : ''}{b.b_year ?? ''}</div>
          }
          {d.d_year &&
            <div>Died: {d.d_month ? `${monthNames[d.d_month-1]} ` : ''}{d.d_day ? `${d.d_day}, ` : ''}{d.d_year ?? ''}</div>
          }
        </div>
      </div>
    </div>
  )
}

function MediaImageTree({mediaID, bloodRel}) {
  // const {data: url} = useMediaUrl(mediaID);
  // const {data: iconUrl, isError: iconError} = useMediaUrl(mediaID, {icon: true});
  // const {data: fullUrl} = useMediaUrl(mediaID, {enabled: iconError});

  // const url = iconUrl ?? fullUrl;
  const {data: iconUrl, isError: iconError} = useMediaUrl(mediaID, {
    icon: true,
    enabled: mediaID !== "S_logo_PNG.png"
  });

  const {data: fullUrl} = useMediaUrl(mediaID, {
    enabled: mediaID === "S_logo_PNG.png" || iconError
  });

  const url = iconUrl ?? fullUrl;

  return (
    <image
      href={url}
      x={-IMG_RAD}
      y={IMG_VERT_OFFSET}
      width={2*IMG_RAD}
      height={2*IMG_RAD}
      clipPath={`circle(${IMG_RAD}px)`}
      preserveAspectRatio="xMidYMid slice"
    />
  )
}

function FullScreen ({fullScreen, setFullScreen}) {
  return (
    <div className={`fullscreen-button-tree ${fullScreen?'in-fullscreen':''}`}>
      <button onClick={() => setFullScreen(!fullScreen)}>
        {!fullScreen && 
          <svg
            viewBox="0 0 24 24"
            width="24"
            height="24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <path d="M9 3H3v6" />
            <path d="M15 3h6v6" />
            <path d="M9 21H3v-6" />
            <path d="M15 21h6v-6" />
          </svg>
        }
        {fullScreen &&
          <svg
            viewBox="0 0 24 24"
            width="24"
            height="24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <path d="M3 15h6v6" />
            <path d="M21 15h-6v6" />
            <path d="M3 9h6v-6" />
            <path d="M21 9h-6v-6" />
          </svg>
        }
      </button>
    </div>
  )
}